export interface AdCreativeProviderSourceApiCall {
  path: string;
  method: 'GET' | 'POST';
  projectId: string;
  body?: Record<string, unknown>;
}

export interface AdCreativeProviderSourceSyncPreflightRequest {
  projectId: string;
  provider: 'meta';
  providerAccountIds: string[];
  scope: {
    mode: 'selected_campaigns';
    campaignIds: string[];
    includeInactiveAds: boolean;
  } | {
    mode: 'all_current';
    includeInactiveAds: boolean;
  };
  funnelAssociationScope: 'segmently_associated' | 'all';
  mirrorMedia: boolean;
  associationWindow?: { from: string; to: string };
}

export interface AdCreativeProviderSourceSyncStartRequest {
  projectId: string;
  preflightToken: string;
  idempotencyKey?: string;
}

export interface AdCreativeProviderSourceCandidatePromotionPreflightRequest {
  projectId: string;
  candidateIds: string[];
  associationWindow: { from: string; to: string };
}

export interface AdCreativeProviderSourceReportRowPreflightRequest {
  projectId: string;
  providerAccountId: string;
  providerAdId: string;
  expectedProviderCreativeId?: string;
  associationWindow: { from: string; to: string };
}

/**
 * Pure capability/cost preflight for one immutable provider snapshot.
 * The server rereads the snapshot; user-visible source copy is never accepted
 * from the client.
 */
export interface AdCreativeProviderSourceLanguageDetectionPreflightRequest {
  projectId: string;
  sourceId: string;
  snapshotId: string;
  model?: string;
}

/** Starts the exact language-detection input sealed by the preflight. */
export interface AdCreativeProviderSourceLanguageDetectionStartRequest {
  projectId: string;
  sourceId: string;
  snapshotId: string;
  model: string;
  preflightToken: string;
  expectedInputManifestHash: string;
}

export interface AdCreativeProviderSourceTaxonomyPreflightRequest {
  projectId: string;
  sourceIds: string[];
  model?: string;
}

export interface AdCreativeProviderSourceTaxonomyStartRequest {
  projectId: string;
  preflightToken: string;
  expectedInputHash: string;
  model: string;
}

export interface AdCreativeProviderSourceTaxonomyReviewRequest {
  projectId: string;
  sourceId: string;
  sourceSnapshotId: string;
  taxonomySnapshotId: string;
  decision: 'approve' | 'reject';
  taxonomy?: object;
  note?: string;
}

export function buildProviderCampaignPageCall(
  projectId: string,
  adAccountId: string,
  options: { limit?: number; cursor?: string } = {},
): AdCreativeProviderSourceApiCall {
  const params = new URLSearchParams({ projectId, adAccountId });
  if (options.limit) params.set('limit', String(options.limit));
  if (options.cursor) params.set('cursor', options.cursor);
  return {
    path: `/api/meta-ads/campaigns?${params.toString()}`,
    method: 'GET',
    projectId,
  };
}

export function buildProviderSourceSyncPreflightCall(
  request: AdCreativeProviderSourceSyncPreflightRequest,
): AdCreativeProviderSourceApiCall {
  return {
    path: '/api/agent/ad-creative/source-syncs/preflight',
    method: 'POST',
    projectId: request.projectId,
    body: { ...request },
  };
}

export function buildProviderSourceSyncCall(
  request: AdCreativeProviderSourceSyncStartRequest,
): AdCreativeProviderSourceApiCall {
  return {
    path: '/api/agent/ad-creative/source-syncs',
    method: 'POST',
    projectId: request.projectId,
    body: { ...request },
  };
}

export function buildProviderSourceSyncProgressCall(
  projectId: string,
  syncRunId: string,
): AdCreativeProviderSourceApiCall {
  const params = new URLSearchParams({ projectId });
  return {
    path: `/api/agent/ad-creative/source-syncs/${encodeURIComponent(syncRunId)}?${params.toString()}`,
    method: 'GET',
    projectId,
  };
}

export function buildProviderSourceSyncCancelCall(
  projectId: string,
  syncRunId: string,
): AdCreativeProviderSourceApiCall {
  return {
    path: `/api/agent/ad-creative/source-syncs/${encodeURIComponent(syncRunId)}/cancel`,
    method: 'POST',
    projectId,
    body: { projectId },
  };
}

export function buildProviderSourceSyncResumeCall(
  projectId: string,
  syncRunId: string,
): AdCreativeProviderSourceApiCall {
  return {
    path: `/api/agent/ad-creative/source-syncs/${encodeURIComponent(syncRunId)}/resume`,
    method: 'POST',
    projectId,
    body: { projectId },
  };
}

export function buildProviderSourceListCall(
  projectId: string,
  options: { providerAccountId?: string; limit?: number; cursor?: string } = {},
): AdCreativeProviderSourceApiCall {
  const params = new URLSearchParams({ projectId });
  if (options.providerAccountId) params.set('providerAccountId', options.providerAccountId);
  if (options.limit) params.set('limit', String(options.limit));
  if (options.cursor) params.set('cursor', options.cursor);
  return {
    path: `/api/agent/ad-creative/sources?${params.toString()}`,
    method: 'GET',
    projectId,
  };
}

export function buildProviderSourceCandidateListCall(
  projectId: string,
  providerAccountId: string,
  options: { limit?: number; cursor?: string } = {},
): AdCreativeProviderSourceApiCall {
  const params = new URLSearchParams({ projectId, providerAccountId });
  if (options.limit) params.set('limit', String(options.limit));
  if (options.cursor) params.set('cursor', options.cursor);
  return {
    path: `/api/agent/ad-creative/source-candidates?${params.toString()}`,
    method: 'GET',
    projectId,
  };
}

export function buildProviderSourceCandidatePromotionPreflightCall(
  request: AdCreativeProviderSourceCandidatePromotionPreflightRequest,
): AdCreativeProviderSourceApiCall {
  return {
    path: '/api/agent/ad-creative/source-candidates/preflight',
    method: 'POST',
    projectId: request.projectId,
    body: { ...request },
  };
}

export function buildProviderSourceReportRowPreflightCall(
  request: AdCreativeProviderSourceReportRowPreflightRequest,
): AdCreativeProviderSourceApiCall {
  return {
    path: '/api/agent/ad-creative/source-syncs/report-row-preflight',
    method: 'POST',
    projectId: request.projectId,
    body: { ...request },
  };
}

export function buildProviderSourceDetailCall(
  projectId: string,
  sourceId: string,
): AdCreativeProviderSourceApiCall {
  const params = new URLSearchParams({ projectId });
  return {
    path: `/api/agent/ad-creative/sources/${encodeURIComponent(sourceId)}?${params.toString()}`,
    method: 'GET',
    projectId,
  };
}

export function buildProviderSourceBindingsCall(
  projectId: string,
  sourceId: string,
  options: { limit?: number; cursor?: string } = {},
): AdCreativeProviderSourceApiCall {
  const params = new URLSearchParams({ projectId });
  if (options.limit) params.set('limit', String(options.limit));
  if (options.cursor) params.set('cursor', options.cursor);
  return {
    path: `/api/agent/ad-creative/sources/${encodeURIComponent(sourceId)}/bindings?${params.toString()}`,
    method: 'GET',
    projectId,
  };
}

export function buildProviderSourceLanguageEvidenceCall(
  projectId: string,
  sourceId: string,
  options: { limit?: number; cursor?: string } = {},
): AdCreativeProviderSourceApiCall {
  const params = new URLSearchParams({ projectId });
  if (options.limit) params.set('limit', String(options.limit));
  if (options.cursor) params.set('cursor', options.cursor);
  return {
    path: `/api/agent/ad-creative/sources/${encodeURIComponent(sourceId)}/language-evidence?${params.toString()}`,
    method: 'GET',
    projectId,
  };
}

export function buildProviderSourceLanguageDetectionPreflightCall(
  request: AdCreativeProviderSourceLanguageDetectionPreflightRequest,
): AdCreativeProviderSourceApiCall {
  const { sourceId, ...body } = request;
  return {
    path: `/api/agent/ad-creative/sources/${encodeURIComponent(sourceId)}/language-detection/preflight`,
    method: 'POST',
    projectId: request.projectId,
    body,
  };
}

export function buildProviderSourceLanguageDetectionStartCall(
  request: AdCreativeProviderSourceLanguageDetectionStartRequest,
): AdCreativeProviderSourceApiCall {
  const { sourceId, ...body } = request;
  return {
    path: `/api/agent/ad-creative/sources/${encodeURIComponent(sourceId)}/language-detections`,
    method: 'POST',
    projectId: request.projectId,
    body,
  };
}

export function buildProviderSourceTaxonomyPreflightCall(
  request: AdCreativeProviderSourceTaxonomyPreflightRequest,
): AdCreativeProviderSourceApiCall {
  return {
    path: '/api/agent/ad-creative/sources/taxonomy/preflight',
    method: 'POST',
    projectId: request.projectId,
    body: { ...request },
  };
}

export function buildProviderSourceTaxonomyStartCall(
  request: AdCreativeProviderSourceTaxonomyStartRequest,
): AdCreativeProviderSourceApiCall {
  return {
    path: '/api/agent/ad-creative/sources/taxonomy-analyses',
    method: 'POST',
    projectId: request.projectId,
    body: { ...request },
  };
}

export function buildProviderSourceTaxonomyReadCall(
  projectId: string,
  sourceId: string,
): AdCreativeProviderSourceApiCall {
  const params = new URLSearchParams({ projectId });
  return {
    path: `/api/agent/ad-creative/sources/${encodeURIComponent(sourceId)}/taxonomy?${params.toString()}`,
    method: 'GET',
    projectId,
  };
}

export function buildProviderSourceTaxonomyReviewCall(
  request: AdCreativeProviderSourceTaxonomyReviewRequest,
): AdCreativeProviderSourceApiCall {
  const { sourceId, ...body } = request;
  return {
    path: `/api/agent/ad-creative/sources/${encodeURIComponent(sourceId)}/taxonomy-reviews`,
    method: 'POST',
    projectId: request.projectId,
    body,
  };
}

export function buildProviderSourceLanguageConfirmationCall(request: {
  projectId: string;
  sourceId: string;
  snapshotId: string;
  sourceBindingId: string;
  providerBaselineEvidenceId?: string;
  locale: string;
}): AdCreativeProviderSourceApiCall {
  return {
    path: `/api/agent/ad-creative/sources/${encodeURIComponent(request.sourceId)}/confirm-language`,
    method: 'POST',
    projectId: request.projectId,
    body: {
      projectId: request.projectId,
      snapshotId: request.snapshotId,
      sourceBindingId: request.sourceBindingId,
      ...(request.providerBaselineEvidenceId
        ? { providerBaselineEvidenceId: request.providerBaselineEvidenceId }
        : {}),
      locale: request.locale,
    },
  };
}

export function buildProviderSourceBaselineSelectionCall(request: {
  projectId: string;
  sourceId: string;
  snapshotId: string;
  sourceBindingId: string;
  candidateId: string;
  candidateHash: string;
}): AdCreativeProviderSourceApiCall {
  const { sourceId, ...body } = request;
  return {
    path: `/api/agent/ad-creative/sources/${encodeURIComponent(sourceId)}/select-baseline`,
    method: 'POST',
    projectId: request.projectId,
    body,
  };
}

export function buildProviderSourceVariationDraftCall(request: {
  projectId: string;
  sourceId: string;
  snapshotId: string;
  sourceBindingId: string;
  providerBaselineEvidenceId?: string;
  sourceLocale: string;
  targetLocales?: string[];
  idempotencyKey?: string;
}): AdCreativeProviderSourceApiCall {
  const { sourceId, ...body } = request;
  return {
    path: `/api/agent/ad-creative/sources/${encodeURIComponent(sourceId)}/create-variation-draft`,
    method: 'POST',
    projectId: request.projectId,
    body,
  };
}
